import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiResponse } from '../interfaces';
import {
    CreatePaymentDto,
    ExecutorBalance,
    SmsBalance,
    Payment,
    PaymentMethod,
    PaymentStatus,
    PaymentSummary,
    PaymentType,
    RetryPaymentDto,
    StatusChangeType,
    Transaction,
    TransactionFiltersDto,
    TransactionStats,
    TransactionType,
    UpdatePaymentStatusDto,
    Wallet,
    WalletFiltersDto,
    WalletSummary,
    WalletTransaction,
    WalletTransactionType
} from '../models/payment.model';

@Injectable({
  providedIn: 'root'
})
export class CompanyPaymentService {
  private baseUrl = '/api/company/payments';

  constructor(private http: HttpClient) {}

  // =================== TRANSACTION OPERATIONS ===================

  getTransactions(filters?: TransactionFiltersDto): Observable<ApiResponse<Transaction[]>> {
    let params = new HttpParams();
    if (filters) {
      if (filters.page !== undefined && filters.page !== null) {
        params = params.set('page', filters.page.toString());
      }
      if (filters.limit !== undefined && filters.limit !== null) {
        params = params.set('limit', filters.limit.toString());
      }

      const dateFrom = filters.dateFrom || (filters as any).startDate;
      const dateTo = filters.dateTo || (filters as any).endDate;

      if (dateFrom) params = params.set('dateFrom', dateFrom);
      if (dateTo) params = params.set('dateTo', dateTo);
      if (filters.orderId !== undefined && filters.orderId !== null) {
        params = params.set('orderId', filters.orderId.toString());
      }
      if (filters.clientId !== undefined && filters.clientId !== null) {
        params = params.set('clientId', filters.clientId.toString());
      }
      if (filters.driverId !== undefined && filters.driverId !== null) {
        params = params.set('driverId', filters.driverId.toString());
      }
      if (filters.status) params = params.set('status', filters.status);
      if (filters.gatewayType) params = params.set('gatewayType', filters.gatewayType);
      if (filters.methodCode) params = params.set('methodCode', filters.methodCode);
      if (filters.typeCode) params = params.set('typeCode', filters.typeCode);
      if (filters.search) params = params.set('search', filters.search);
      if (filters.minAmount !== undefined && filters.minAmount !== null) {
        params = params.set('minAmount', filters.minAmount.toString());
      }
      if (filters.maxAmount !== undefined && filters.maxAmount !== null) {
        params = params.set('maxAmount', filters.maxAmount.toString());
      }
    }
    return this.http.get<ApiResponse<Transaction[]>>(`${this.baseUrl}/transactions`, { params });
  }

  getTransactionById(id: number): Observable<ApiResponse<Transaction>> {
    return this.http.get<ApiResponse<Transaction>>(`${this.baseUrl}/transactions/${id}`);
  }

  retryTransaction(id: number, dto?: RetryPaymentDto): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/transactions/${id}/retry`, dto || {});
  }

  getTransactionStats(): Observable<ApiResponse<TransactionStats>> {
    return this.http.get<ApiResponse<TransactionStats>>(`${this.baseUrl}/transactions/stats/summary`);
  }

  // =================== PAYMENT OPERATIONS (legacy) ===================

  createPayment(createDto: CreatePaymentDto): Observable<ApiResponse<Payment>> {
    return this.http.post<ApiResponse<Payment>>(`${this.baseUrl}/orders`, createDto);
  }

  updatePaymentStatus(id: number, updateDto: UpdatePaymentStatusDto): Observable<ApiResponse<Payment>> {
    return this.http.put<ApiResponse<Payment>>(`${this.baseUrl}/orders/${id}/status`, updateDto);
  }

  /**
   * Refund a completed payment via its gateway (Stripe/Exactly) - real money
   * back to the client's card, not just a status flip. Omit amount for a
   * full refund.
   */
  refundPayment(id: number, data: { amount?: number; reason?: string }): Observable<ApiResponse<Payment>> {
    return this.http.post<ApiResponse<Payment>>(`${this.baseUrl}/orders/${id}/refund`, data);
  }

  deletePayment(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/orders/${id}`);
  }

  /**
   * Export the current filtered transaction list as CSV. There is no
   * dedicated export endpoint for this list, so this fetches every matching
   * row (up to 10000, matching the cap used by the Reports module) and
   * builds the CSV client-side.
   */
  exportTransactionsCsv(filters?: TransactionFiltersDto): Observable<Transaction[]> {
    return this.getTransactions({ ...filters, page: 1, limit: 10000 }).pipe(
      map(res => (res.success && Array.isArray(res.data)) ? res.data : []),
    );
  }

  // =================== WALLET OPERATIONS ===================

  /**
   * Manually top up the company's own corporate_balance wallet.
   */
  topUpCorporateBalance(data: { amount: number; description?: string }): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/wallets/corporate-balance/topup`, data);
  }

  /**
   * Get all company wallets (earnings, payouts, corporate_balance)
   */
  getWallets(): Observable<ApiResponse<Wallet[]>> {
    return this.http.get<ApiResponse<Wallet[]>>(`${this.baseUrl}/wallets`);
  }

  /**
   * Get wallet by type
   */
  getWalletByType(walletType: string): Observable<ApiResponse<Wallet>> {
    return this.http.get<ApiResponse<Wallet>>(`${this.baseUrl}/wallets/${walletType}`);
  }

  /**
   * Get wallet transactions with filters
   */
  getWalletTransactions(walletType: string, filters?: WalletFiltersDto): Observable<ApiResponse<WalletTransaction[]>> {
    let params = new HttpParams();

    if (filters) {
      if (filters.page) params = params.set('page', filters.page.toString());
      if (filters.limit) params = params.set('limit', filters.limit.toString());
      const dateFrom = (filters as any).dateFrom || filters.startDate;
      const dateTo = (filters as any).dateTo || filters.endDate;
      if (dateFrom) params = params.set('dateFrom', dateFrom);
      if (dateTo) params = params.set('dateTo', dateTo);
    }

    return this.http.get<ApiResponse<WalletTransaction[]>>(`${this.baseUrl}/wallets/${walletType}/transactions`, { params });
  }

  /**
   * Get wallet summary for dashboard
   */
  getWalletSummary(targetCompanyId?: number): Observable<ApiResponse<WalletSummary>> {
    let params = new HttpParams();
    if (targetCompanyId !== undefined && targetCompanyId !== null) {
      params = params.set('targetCompanyId', targetCompanyId.toString());
    }
    return this.http.get<ApiResponse<WalletSummary>>(`${this.baseUrl}/wallets/summary`, { params });
  }

  // =================== REFERENCE DATA ===================

  /**
   * Get all reference data (methods, statuses, types)
   */
  getReferenceData(): Observable<ApiResponse<{
    methods: PaymentMethod[];
    statuses: PaymentStatus[];
    types: PaymentType[];
  }>> {
    return this.http.get<ApiResponse<{
      paymentMethods: PaymentMethod[];
      paymentStatuses: PaymentStatus[];
      paymentTypes: PaymentType[];
    }>>(`${this.baseUrl}/reference-data`).pipe(
      map(res => {
        const mapped: ApiResponse<{ methods: PaymentMethod[]; statuses: PaymentStatus[]; types: PaymentType[] }> = {
          ...(res as object),
          data: res.data ? {
            methods: res.data.paymentMethods || [],
            statuses: res.data.paymentStatuses || [],
            types: res.data.paymentTypes || [],
          } : null,
        } as ApiResponse<{ methods: PaymentMethod[]; statuses: PaymentStatus[]; types: PaymentType[] }>;
        return mapped;
      })
    );
  }

  /**
   * Get payment methods
   */
  getPaymentMethods(): Observable<ApiResponse<PaymentMethod[]>> {
    return this.http.get<ApiResponse<PaymentMethod[]>>(`${this.baseUrl}/reference-data/payment-methods`);
  }

  /**
   * Get all payment methods including inactive (admin only)
   */
  getAllPaymentMethods(): Observable<ApiResponse<PaymentMethod[]>> {
    return this.http.get<ApiResponse<PaymentMethod[]>>(`${this.baseUrl}/reference-data/payment-methods/all`);
  }

  /**
   * Toggle payment method active/inactive (admin only, cash and corporate_account are protected)
   */
  togglePaymentMethod(id: number): Observable<ApiResponse<PaymentMethod>> {
    return this.http.patch<ApiResponse<PaymentMethod>>(`${this.baseUrl}/reference-data/payment-methods/${id}/toggle`, {});
  }

  /**
   * Get payment statuses
   */
  getPaymentStatuses(): Observable<ApiResponse<PaymentStatus[]>> {
    return this.http.get<ApiResponse<PaymentStatus[]>>(`${this.baseUrl}/reference-data/payment-statuses`);
  }

  /**
   * Get payment types
   */
  getPaymentTypes(): Observable<ApiResponse<PaymentType[]>> {
    return this.http.get<ApiResponse<PaymentType[]>>(`${this.baseUrl}/reference-data/payment-types`);
  }

  /**
   * Get transaction types
   */
  getTransactionTypes(): Observable<ApiResponse<TransactionType[]>> {
    return this.http.get<ApiResponse<TransactionType[]>>(`${this.baseUrl}/reference-data/transaction-types`);
  }

  /**
   * Get wallet transaction types
   */
  getWalletTransactionTypes(): Observable<ApiResponse<WalletTransactionType[]>> {
    return this.http.get<ApiResponse<WalletTransactionType[]>>(`${this.baseUrl}/reference-data/wallet-transaction-types`);
  }

  /**
   * Get status change types
   */
  getStatusChangeTypes(): Observable<ApiResponse<StatusChangeType[]>> {
    return this.http.get<ApiResponse<StatusChangeType[]>>(`${this.baseUrl}/reference-data/status-change-types`);
  }

  // =================== EXECUTOR WALLET ===================

  getExecutorWallet(driverId: number, forCompanyId?: number, allCompanies = false): Observable<ApiResponse<any>> {
    let q = '';
    if (allCompanies) q = '?allCompanies=true';
    else if (forCompanyId) q = `?forCompanyId=${forCompanyId}`;
    return this.http.get<ApiResponse<any>>(`/api/company/executors/${driverId}/wallet${q}`);
  }

  getExecutorWalletTransactions(driverId: number, filters?: {
    page?: number; limit?: number; dateFrom?: string; dateTo?: string; operationType?: string;
  }, forCompanyId?: number, allCompanies = false): Observable<ApiResponse<any>> {
    let params = new HttpParams();
    if (allCompanies) params = params.set('allCompanies', 'true');
    else if (forCompanyId) params = params.set('forCompanyId', forCompanyId.toString());
    if (filters) {
      if (filters.page) params = params.set('page', filters.page.toString());
      if (filters.limit) params = params.set('limit', filters.limit.toString());
      if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params = params.set('dateTo', filters.dateTo);
      if (filters.operationType) params = params.set('operationType', filters.operationType);
    }
    return this.http.get<ApiResponse<any>>(`/api/company/executors/${driverId}/wallet/transactions`, { params });
  }

  /**
   * Real bug fixed 2026-08-31 (general-docs/TODO-2026-08-31-invoices-fleet-analysis/
   * 02-driver-wallet-balance-bug.md): this never sent forCompanyId, unlike
   * getExecutorWallet/getExecutorWalletTransactions above - so a manual credit
   * made by a company that isn't the executor's own (e.g. a main company
   * managing a fleet executor) silently landed in a different wallet row than
   * the one everything else reads. Callers must now pass the executor's own
   * companyId (the same value already passed to getExecutorWallet/
   * getExecutorWalletTransactions).
   */
  manualCreditExecutor(driverId: number, dto: { amount: number; subtype: string; description: string }, forCompanyId?: number): Observable<ApiResponse<any>> {
    const params = forCompanyId ? `?forCompanyId=${forCompanyId}` : '';
    return this.http.post<ApiResponse<any>>(`/api/company/executors/${driverId}/wallet/manual-credit${params}`, dto);
  }

  manualDebitExecutor(driverId: number, dto: { amount: number; subtype: string; description: string }, forCompanyId?: number): Observable<ApiResponse<any>> {
    const params = forCompanyId ? `?forCompanyId=${forCompanyId}` : '';
    return this.http.post<ApiResponse<any>>(`/api/company/executors/${driverId}/wallet/manual-debit${params}`, dto);
  }

  // =================== DASHBOARD SUMMARY ===================

  /**
   * Get payment summary for dashboard
   */
  getPaymentSummary(targetCompanyId?: number): Observable<ApiResponse<PaymentSummary>> {
    let params = new HttpParams();
    if (targetCompanyId !== undefined && targetCompanyId !== null) {
      params = params.set('targetCompanyId', targetCompanyId.toString());
    }
    return this.http.get<ApiResponse<PaymentSummary>>(`${this.baseUrl}/summary`, { params });
  }

  /**
   * Get all executor wallet balances for the company
   */
  getExecutorsBalances(): Observable<ApiResponse<ExecutorBalance[]>> {
    return this.http.get<ApiResponse<ExecutorBalance[]>>(`${this.baseUrl}/executors/balances`);
  }

  /**
   * Get current SMS gateway account balance (for the dashboard SMS balance card)
   */
  getSmsBalance(): Observable<ApiResponse<SmsBalance>> {
    return this.http.get<ApiResponse<SmsBalance>>(`${this.baseUrl}/sms-balance`);
  }
}
